import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Clock, Check, X, ChefHat, Bell, AlertTriangle, Utensils, Bike, Maximize2 } from "lucide-react";
import { api, inr, apiError } from "@/lib/api";

const COLS = [
  { key: "new", label: "New", statuses: ["placed"], tone: "bg-amber-50 border-amber-200" },
  { key: "accepted", label: "Accepted", statuses: ["confirmed"], tone: "bg-blue-50 border-blue-200" },
  { key: "preparing", label: "Preparing", statuses: ["preparing"], tone: "bg-indigo-50 border-indigo-200" },
  { key: "ready", label: "Ready", statuses: ["ready"], tone: "bg-emerald-50 border-emerald-200" },
  { key: "pickedup", label: "Picked Up", statuses: ["picked_up", "on_the_way"], tone: "bg-slate-50 border-slate-200" },
];
const REASONS = ["Restaurant too busy", "Item unavailable", "Kitchen issue", "Closing soon", "Other"];

function ageMin(o) {
  return Math.floor((Date.now() - new Date(o.created_at).getTime()) / 60000);
}

function OrderCard({ o, onAccept, onReject, onPrep, onReady, onView, kds }) {
  const age = ageMin(o);
  const overdue = ["preparing", "confirmed"].includes(o.status) && age > (o.prep_time || 25);
  return (
    <div className={`rounded-xl border bg-white p-3 ${overdue ? "ring-2 ring-bad/40" : ""}`} data-testid={`board-order-${o.code}`}>
      <div className="flex items-center justify-between">
        <span className={`font-bold ${kds ? "text-lg" : "text-sm"}`}>#{o.code?.slice(-4)}</span>
        <span className={`text-[11px] flex items-center gap-1 ${overdue ? "text-bad font-bold" : "text-ink-600"}`}><Clock size={11} /> {age}m {overdue && "· overdue"}</span>
      </div>
      {!kds && <div className="text-xs text-ink-600 mt-0.5">{o.customer_name} · {inr(o.total)} · {o.payment_method?.toUpperCase()}</div>}
      <div className={`mt-2 space-y-1 ${kds ? "text-base" : "text-xs"}`}>
        {o.items.map((it, i) => (
          <div key={i} className="text-ink">
            <span className="font-bold">{it.qty}×</span> {it.name}
            {it.variant && <span className="text-ink-600"> · {it.variant}</span>}
            {it.addons?.length > 0 && <div className="text-[11px] text-coral pl-4">+ {it.addons.map((a) => a.name).join(", ")}</div>}
            {it.spice && <div className="text-[11px] text-ink-600 pl-4">Spice: {it.spice}</div>}
            {it.instructions && <div className="text-[11px] text-amber-700 pl-4">✎ {it.instructions}</div>}
          </div>
        ))}
      </div>
      {o.instructions && <div className="mt-2 text-[11px] bg-amber-50 text-amber-800 rounded-lg px-2 py-1 flex items-start gap-1"><AlertTriangle size={11} className="mt-0.5" /> {o.instructions}</div>}
      {o.delivery_partner && !kds && <div className="mt-1.5 text-[11px] text-ink-600 flex items-center gap-1"><Bike size={11} /> {o.delivery_partner.name} · {o.status.replace("_", " ")}</div>}
      <div className="flex gap-1.5 mt-2 flex-wrap">
        {o.status === "placed" && <>
          <button onClick={() => onAccept(o)} className="flex-1 bg-veg text-white font-bold text-xs py-1.5 rounded-lg tap flex items-center justify-center gap-1" data-testid={`accept-${o.code}`}><Check size={13} /> Accept</button>
          <button onClick={() => onReject(o)} className="flex-1 bg-red-50 text-bad font-bold text-xs py-1.5 rounded-lg tap flex items-center justify-center gap-1" data-testid={`reject-${o.code}`}><X size={13} /> Reject</button>
        </>}
        {o.status === "confirmed" && <button onClick={() => onPrep(o)} className="flex-1 bg-coral text-white font-bold text-xs py-1.5 rounded-lg tap flex items-center justify-center gap-1" data-testid={`prep-${o.code}`}><ChefHat size={13} /> Start preparing</button>}
        {o.status === "preparing" && <button onClick={() => onReady(o)} className="flex-1 bg-emerald-600 text-white font-bold text-xs py-1.5 rounded-lg tap flex items-center justify-center gap-1" data-testid={`ready-${o.code}`}><Bell size={13} /> Mark ready</button>}
        {!kds && <button onClick={() => onView(o)} className="text-xs font-semibold text-ink-600 px-2 py-1.5" data-testid={`view-${o.code}`}>Details</button>}
      </div>
    </div>
  );
}

export function LiveOrderBoard({ orders, completedCount, onChanged, kds }) {
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState(REASONS[0]);
  const [view, setView] = useState(null);
  const [full, setFull] = useState(false);
  const prevCount = useRef(orders?.length || 0);

  useEffect(() => {
    const news = orders.filter((o) => o.status === "placed").length;
    if (news > prevCount.current) { try { new Audio("data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQ==").play().catch(() => {}); } catch (e) {} }
    prevCount.current = news;
  }, [orders]);

  const move = async (o, status, extra = {}) => {
    try { await api.post(`/restaurant/orders/${o.id}/status`, { status, ...extra }); toast.success(`Order #${o.code?.slice(-4)} → ${status.replace("_", " ")}`); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const accept = (o) => move(o, "confirmed");
  const prep = (o) => { const t = prompt("Preparation time (minutes)", o.prep_time || 20); if (t == null) return; move(o, "preparing", { prep_time: Number(t) || 20 }); };
  const ready = (o) => move(o, "ready");
  const doReject = async () => { await move(rejectFor, "cancelled", { reason }); setRejectFor(null); };

  const handlers = { onAccept: accept, onReject: setRejectFor, onPrep: prep, onReady: ready, onView: setView };
  const colsToShow = kds ? COLS.slice(0, 4) : COLS;

  const board = (
    <div className={`grid gap-3 ${kds ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-4" : "grid-cols-2 md:grid-cols-3 lg:grid-cols-5"}`}>
      {colsToShow.map((c) => {
        const items = orders.filter((o) => c.statuses.includes(o.status));
        return (
          <div key={c.key} className={`rounded-2xl border ${c.tone} p-2 min-h-[120px]`} data-testid={`col-${c.key}`}>
            <div className="flex items-center justify-between px-1 py-1.5"><span className="font-bold text-sm text-ink">{c.label}</span><span className="text-xs font-bold bg-white rounded-full px-2 py-0.5">{items.length}</span></div>
            <div className="space-y-2">
              {items.map((o) => <OrderCard key={o.id} o={o} {...handlers} kds={kds} />)}
              {items.length === 0 && <p className="text-[11px] text-ink-600 text-center py-3">Empty</p>}
            </div>
          </div>
        );
      })}
      {!kds && (
        <div className="rounded-2xl border bg-emerald-50 border-emerald-200 p-2" data-testid="col-completed">
          <div className="flex items-center justify-between px-1 py-1.5"><span className="font-bold text-sm text-ink">Completed</span><span className="text-xs font-bold bg-white rounded-full px-2 py-0.5">{completedCount}</span></div>
          <p className="text-[11px] text-ink-600 text-center py-3">{completedCount} delivered</p>
        </div>
      )}
    </div>
  );

  return (
    <div className={full ? "fixed inset-0 z-[80] bg-surface p-4 overflow-y-auto" : ""} data-testid="live-order-board">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-ink flex items-center gap-2">{kds ? <><Utensils size={18} /> Kitchen Display</> : <>Live Order Board ({orders.length} active)</>}</h3>
        <button onClick={() => setFull(!full)} className="text-xs font-semibold text-ink-600 flex items-center gap-1" data-testid="fullscreen-toggle"><Maximize2 size={14} /> {full ? "Exit" : "Full screen"}</button>
      </div>
      {board}

      {rejectFor && (
        <div className="fixed inset-0 z-[85] bg-black/50 flex items-center justify-center p-4" onClick={() => setRejectFor(null)}>
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="reject-modal">
            <h3 className="font-bold text-ink mb-3">Reject order #{rejectFor.code?.slice(-4)}</h3>
            <div className="space-y-1.5">{REASONS.map((r) => <label key={r} className="flex items-center gap-2 text-sm cursor-pointer"><input type="radio" checked={reason === r} onChange={() => setReason(r)} className="accent-coral" /> {r}</label>)}</div>
            <div className="flex gap-2 mt-4"><button onClick={() => setRejectFor(null)} className="flex-1 border border-border rounded-xl py-2 text-sm font-semibold">Cancel</button><button onClick={doReject} className="flex-1 bg-bad text-white rounded-xl py-2 text-sm font-bold" data-testid="confirm-reject">Reject order</button></div>
          </div>
        </div>
      )}

      {view && (
        <div className="fixed inset-0 z-[85] bg-black/50 flex items-center justify-center p-4" onClick={() => setView(null)}>
          <div className="bg-white rounded-2xl p-5 max-w-md w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="order-detail-modal">
            <div className="flex items-center justify-between mb-2"><h3 className="font-bold text-ink">Order #{view.code?.slice(-4)}</h3><button onClick={() => setView(null)}><X size={18} /></button></div>
            <div className="text-sm text-ink-600 mb-3">{view.customer_name} · {view.payment_method?.toUpperCase()} · {inr(view.total)}</div>
            <div className="space-y-1 mb-3">{view.items.map((it, i) => <div key={i} className="flex justify-between text-sm"><span>{it.qty}× {it.name}{it.variant ? ` (${it.variant})` : ""}</span><span>{inr(it.line_total || it.unit_price * it.qty)}</span></div>)}</div>
            <div className="border-t border-border pt-2 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-ink-600">Food cost</span><span>{inr(view.food_subtotal ?? view.subtotal)}</span></div>
              {view.discount > 0 && <div className="flex justify-between text-veg"><span>Discount</span><span>- {inr(view.discount)}</span></div>}
              <div className="flex justify-between"><span className="text-ink-600">Delivery fee</span><span>{inr(view.delivery_fee)}</span></div>
              <div className="flex justify-between font-bold"><span>Total paid</span><span>{inr(view.total)}</span></div>
            </div>
            {view.delivery_partner && <div className="mt-3 text-xs bg-surface rounded-xl p-2.5 flex items-center gap-2"><Bike size={14} /> {view.delivery_partner.name} · {view.delivery_partner.vehicle} · {view.status.replace("_", " ")}</div>}
            <div className="mt-3">
              <div className="text-xs font-bold text-ink-600 mb-1">Timeline</div>
              {view.status_history?.map((h, i) => <div key={i} className="text-[11px] text-ink-600 flex justify-between"><span className="capitalize">{h.status.replace("_", " ")}</span><span>{h.at?.slice(11, 16)}</span></div>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
